export const SUPPORTED_LOCALES = [
  'he',
  'ar',
  'de',
  'fr',
  'es',
  'it',
  'zh-CN',
  'ru',
  'pt',
  'tr',
  'el',
  'lt',
  'sk',
  'hi',
  'hu',
  'nl',
  'nb-NO',
  'ro',
  'sv',
  'th',
  'vi',
  'ja',
  'ko-KR',
  'pl',
  'en-US',
] as const

export const LOCALE_FLAGS: Record<string, string> = {
  he: 'il',
  ar: 'ae',
  de: 'de',
  fr: 'fr',
  es: 'es',
  it: 'it',
  'zh-CN': 'cn',
  ru: 'ru',
  pt: 'pt',
  tr: 'tr',
  el: 'gr',
  lt: 'lt',
  sk: 'sk',
  hi: 'in',
  hu: 'hu',
  nl: 'nl',
  'nb-NO': 'no',
  ro: 'ro',
  sv: 'se',
  th: 'th',
  vi: 'vn',
  ja: 'jp',
  'ko-KR': 'kr',
  pl: 'pl',
  'en-US': 'us',
}

// Quasar's own `nativeName` is wrong for a couple of packs, and the picker shows it
// verbatim. Override those here rather than patching node_modules.
//   lt → Quasar says "Lithuanian" (English, not the native name like every other entry)
//   hi → Quasar says "अमेरिकी अंग्रेज़ी", which literally means "American English"
export const LOCALE_NATIVE_NAME_OVERRIDES: Record<string, string> = {
  lt: 'Lietuvių',
  hi: 'हिन्दी',
}

/** Flag (country) code for a locale, accepting either a full code or its base. */
export function flagForLocale(code: string): string {
  if (!code) return ''
  if (LOCALE_FLAGS[code]) return LOCALE_FLAGS[code]
  const base = code.split('-')[0]
  if (LOCALE_FLAGS[base]) return LOCALE_FLAGS[base]
  const full = Object.keys(LOCALE_FLAGS).find((k) => k.split('-')[0] === base)
  return full ? LOCALE_FLAGS[full] : ''
}

/** Native display name for a locale, preferring our override over Quasar's. */
export function nativeNameFor(isoName: string, quasarNativeName: string): string {
  return (
    LOCALE_NATIVE_NAME_OVERRIDES[isoName] ||
    LOCALE_NATIVE_NAME_OVERRIDES[isoName.split('-')[0]] ||
    quasarNativeName
  )
}

// Must stay in sync with admin and cloud language index mapping.
// 15–24: store-listing languages reserved ahead of app translations (append-only).
// APPEND-ONLY — these indexes are persisted on User.language / Business.menu_language.
// The whole 0–25 range is translated and in SUPPORTED_LOCALES. (An older comment here said
// 22–24 were "hidden until translated"; that stopped being true on 2026-08-09.)
// pl = 25 is the LAST planned index — the language list ends there.
export const LOCALE_SERVER_INDEX: Record<string, number> = {
  'en-US': 0,
  he: 2,
  es: 3,
  de: 4,
  fr: 5,
  it: 6,
  ar: 7,
  'zh-CN': 8,
  'ko-KR': 9,
  ru: 10,
  pt: 11,
  tr: 12,
  el: 13,
  lt: 14,
  sk: 15,
  hi: 16,
  hu: 17,
  nl: 18,
  // Norwegian: Quasar only ships `nb-NO`, so THAT is the app locale code (pickers filter
  // by Quasar's isoName). `no` is kept as an alias — the SERVER resolves index 19 to the
  // string "no" and reads cloud/locales/no.json.
  'nb-NO': 19,
  no: 19,
  ro: 20,
  sv: 21,
  th: 22,
  vi: 23,
  ja: 24,
  pl: 25,
  other: 999,
}

// Reverse of LOCALE_SERVER_INDEX — numeral index → locale code. Mirrors the
// cloud's getLocaleFromIndex (0/1 both → en) so the app UI can obey a user's
// stamped preferred language (User.language).
export const SERVER_INDEX_LOCALE: Record<number, string> = {
  0: 'en-US',
  1: 'en-US',
  2: 'he',
  3: 'es',
  4: 'de',
  5: 'fr',
  6: 'it',
  7: 'ar',
  8: 'zh-CN',
  9: 'ko-KR',
  10: 'ru',
  11: 'pt',
  12: 'tr',
  13: 'el',
  14: 'lt',
  15: 'sk',
  16: 'hi',
  17: 'hu',
  18: 'nl',
  19: 'nb-NO',
  20: 'ro',
  21: 'sv',
  22: 'th',
  23: 'vi',
  24: 'ja',
  25: 'pl',
}

export function localeFromServerIndex(index: number | null | undefined): string {
  if (index === null || index === undefined) return 'en-US'
  return SERVER_INDEX_LOCALE[index] ?? 'en-US'
}

export type MenuLanguageOption = {
  index: number,
  code: string,
  name: string,
  flag: string | null,
}

export function buildMenuLanguageOptions(languages: Array<{ isoName: string, nativeName: string }>): MenuLanguageOption[] {
  const list = languages
    .filter((lang) => SUPPORTED_LOCALES.includes(lang.isoName as (typeof SUPPORTED_LOCALES)[number]))
    .filter((lang) => LOCALE_SERVER_INDEX[lang.isoName] !== undefined)
    .map((lang) => ({
      index: LOCALE_SERVER_INDEX[lang.isoName],
      code: lang.isoName,
      name: lang.nativeName,
      flag: LOCALE_FLAGS[lang.isoName] || 'us',
    }))
    .sort((a, b) => a.index - b.index)

  list.push({ index: 999, code: 'other', name: 'Other', flag: null })

  return list
}
